import React from 'react';
import { Box, Card, Checkbox, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UIFlex from 'components/ui/UIFlex';
import { VocabularyTestBlock, VocabularyTestWord } from 'app/types';
import TestWord from 'pages/VocabularyTest/components/TestWord';
import {
  autoColumnCount,
  DESKTOP_LAYOUT_BREAKPOINT,
  distributeWordsIntoColumns,
  getVocabularyTestTopicKey,
  getVocabularyTestWordKey,
} from 'pages/VocabularyTest/utils';

interface ITestBlockProps {
  testData: VocabularyTestBlock;
  selectedWords: Record<string, boolean | undefined>;
  onToggleWord: (word: VocabularyTestWord) => void;
  onToggleGroup: (words: VocabularyTestWord[]) => void;
  /** Whether the category sidebar (236px) is currently expanded. With it
   * open, a 4-column grid leaves too little room per column and longer
   * entries ("to understand", "to communicate") wrap to a second line,
   * making that chip taller than its row neighbors. Capping at 3 columns
   * while the rail is open keeps columns wide enough to avoid wrapping. */
  railOpen: boolean;
}

const COMPLETE_COLOR = '#3E9B5F';

const TestBlock: React.FC<ITestBlockProps> = ({
  testData,
  selectedWords,
  onToggleWord,
  onToggleGroup,
  railOpen,
}) => {
  const total = testData.words.length;
  const doneCount = testData.words.filter((word) => !!word.status).length;
  const complete = total > 0 && doneCount === total;
  const progressPct = total ? Math.round((doneCount / total) * 100) : 0;

  const allSelected =
    total > 0 &&
    testData.words.every(
      (word) => !!selectedWords[getVocabularyTestWordKey(word)],
    );
  const someSelected = testData.words.some(
    (word) => !!selectedWords[getVocabularyTestWordKey(word)],
  );

  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  // The sidebar (236px) and the generous auto column count are both
  // desktop-only (see the matching DESKTOP_LAYOUT_BREAKPOINT cutoff on
  // CategoryNav/StatusActionsBar/StageNav) — a "tablet-ish" 2-column grid
  // holds all the way up to that cutoff too, since e.g. an iPad Pro at 1024
  // doesn't have room for 3-4 columns of longer words ("grandmother",
  // "grandfather") without wrapping.
  const isTablet = useMediaQuery(
    theme.breakpoints.between('sm', DESKTOP_LAYOUT_BREAKPOINT),
  );
  const columnCount = isPhone
    ? 1
    : isTablet
    ? 2
    : Math.min(autoColumnCount(total), railOpen ? 3 : 4);
  const columns = distributeWordsIntoColumns(testData.words, columnCount);

  return (
    <Box data-cat={getVocabularyTestTopicKey(testData)} mb={3.5} sx={{ scrollMarginTop: 220 }}>
      <Card
        sx={(theme) => ({
          border: '1px solid',
          borderColor: complete
            ? alpha(COMPLETE_COLOR, 0.55)
            : theme.palette.primary.tableBorder,
        })}
      >
        <UIFlex mb={2.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
          <Stack alignItems={'center'} direction={'row'} spacing={1.5}>
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && someSelected}
              onClick={() => onToggleGroup(testData.words)}
            />
            <Typography variant={'h3'}>
              {`${testData.categoryName}. ${testData.subCategoryName}`}
            </Typography>
          </Stack>

          <Box sx={{ flex: 1 }} />

          {complete ? (
            <Stack
              alignItems={'center'}
              direction={'row'}
              spacing={0.75}
              sx={{ color: COMPLETE_COLOR }}
            >
              <CheckCircleIcon fontSize={'small'} />
              <Typography sx={{ fontWeight: 700 }}>{'Пройдено'}</Typography>
            </Stack>
          ) : (
            <Stack alignItems={'center'} direction={'row'} spacing={1.25}>
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 999,
                  height: 7,
                  overflow: 'hidden',
                  width: 84,
                }}
              >
                <Box
                  sx={{
                    bgcolor: 'primary.main',
                    height: '100%',
                    width: `${progressPct}%`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontWeight: 700,
                  minWidth: 46,
                  textAlign: 'right',
                }}
              >
                {`${doneCount}/${total}`}
              </Typography>
            </Stack>
          )}
        </UIFlex>

        <UIFlex alignItems={'flex-start'} gap={2.5}>
          {columns.map((columnWords, columnIndex) => {
            const columnAllSelected = columnWords.every(
              (word) => !!selectedWords[getVocabularyTestWordKey(word)],
            );
            const columnSomeSelected = columnWords.some(
              (word) => !!selectedWords[getVocabularyTestWordKey(word)],
            );

            return (
              <Stack flex={1} gap={1.25} key={columnIndex} minWidth={0}>
                {columns.length > 1 && (
                  <UIFlex jc={'center'} pb={0.25}>
                    <Checkbox
                      checked={columnAllSelected}
                      indeterminate={!columnAllSelected && columnSomeSelected}
                      onClick={() => onToggleGroup(columnWords)}
                      size={'small'}
                    />
                  </UIFlex>
                )}
                {columnWords.map((word) => {
                  const wordKey = getVocabularyTestWordKey(word);
                  return (
                    <TestWord
                      isSelected={!!selectedWords[wordKey]}
                      key={wordKey}
                      onClick={() => onToggleWord(word)}
                      word={word}
                    />
                  );
                })}
              </Stack>
            );
          })}
        </UIFlex>
      </Card>
    </Box>
  );
};

export default TestBlock;
